import { useCallback, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { PageContainer } from '../../components/Layout/PageContainer';
import { SourcingGrid } from './SourcingGrid';
import {
  useCopyItemsToListsMutation,
  useBulkRemoveListItemsMutation,
  useListCompaniesQuery,
  useListPeopleQuery,
  useSourcingListsQuery,
} from '../../services/queries-hooks/SourcingListsQueriesHooks';
import { useCreateSourcingList } from './hooks/useSourcingListActions';
import { ConfirmDeletionDialog } from '../../components/Dialog/ConfirmDialog/ConfirmDeletionDialog';
import { AddToListDialog } from './components/AddToListDialog';
import { sourcingGridApiAtom } from './state/SourcingListsState';
import { EntityType, getEntityLabel } from './util/entityLabels';

interface SourcingContentProps {
  readonly listId: string | undefined;
  readonly entityType?: EntityType;
  readonly listType?: 'static' | 'active';
}

export function SourcingContent({ listId, entityType = 'company', listType }: SourcingContentProps) {
  const companiesQuery = useListCompaniesQuery(listId, entityType);
  const peopleQuery = useListPeopleQuery(listId, entityType);
  const gridApi = useAtomValue(sourcingGridApiAtom);
  const { data: sourcingData } = useSourcingListsQuery();

  const [selectedCount, setSelectedCount] = useState(0);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAddToListOpen, setIsAddToListOpen] = useState(false);

  const bulkRemoveMutation = useBulkRemoveListItemsMutation(listId, entityType);
  const copyItemsMutation = useCopyItemsToListsMutation();
  const createListMutation = useCreateSourcingList();

  const handleSelectionChanged = useCallback((count: number) => {
    setSelectedCount(count);
  }, []);

  const handleDeleteSelected = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    const selectedIds =
      gridApi
        ?.getSelectedNodes()
        .map((n) => n.data?.id)
        .filter(Boolean) ?? [];

    if (selectedIds.length === 0) {
      setIsDeleteDialogOpen(false);
      return;
    }

    bulkRemoveMutation.mutate(selectedIds, {
      onSuccess: () => {
        gridApi?.deselectAll();
        setSelectedCount(0);
      },
      onSettled: () => {
        setIsDeleteDialogOpen(false);
      },
    });
  }, [bulkRemoveMutation, gridApi]);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteDialogOpen(false);
  }, []);

  // ── Add to List ──────────────────────────────────────────────────────────
  const compatibleLists = useMemo(
    () => (sourcingData?.lists ?? []).filter((l) => l.entityType === entityType && l.id !== listId),
    [sourcingData, entityType, listId]
  );

  const handleOpenAddToList = useCallback(() => {
    setIsAddToListOpen(true);
  }, []);

  const handleAddToListConfirm = useCallback(
    async (listIds: string[], newListName?: string) => {
      const selectedIds =
        gridApi
          ?.getSelectedNodes()
          .map((n) => n.data?.id)
          .filter(Boolean) ?? [];

      if (selectedIds.length === 0) {
        return;
      }

      const targetListIds = [...listIds];

      try {
        if (newListName) {
          const newList = await createListMutation.mutateAsync({ name: newListName, entityType });
          targetListIds.push(newList.id);
        }

        if (targetListIds.length === 0) {
          return;
        }

        copyItemsMutation.mutate(
          { itemIds: selectedIds, targetListIds },
          {
            onSettled: () => {
              setIsAddToListOpen(false);
            },
          }
        );
      } catch {
        // createListMutation.onError already shows toast
      }
    },
    [gridApi, entityType, createListMutation, copyItemsMutation]
  );

  const activeQuery = entityType === 'company' ? companiesQuery : peopleQuery;
  const isLoading = activeQuery.isLoading;
  const rowData = activeQuery.data ?? [];

  const entityLabel = getEntityLabel(entityType, selectedCount);

  return (
    <PageContainer sx={{ padding: '0.5rem 0.75rem 1rem' }}>
      <SourcingGrid
        entityType={entityType}
        listType={listType}
        selectedCount={selectedCount}
        rowData={rowData}
        isLoading={isLoading}
        onSelectionChanged={handleSelectionChanged}
        onDeleteSelected={handleDeleteSelected}
        onAddToList={handleOpenAddToList}
      />
      <ConfirmDeletionDialog
        open={isDeleteDialogOpen}
        title={`Delete ${selectedCount} ${entityLabel}?`}
        loading={bulkRemoveMutation.isPending}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      >
        The selected {entityLabel} will be permanently removed from this list.
      </ConfirmDeletionDialog>
      <AddToListDialog
        open={isAddToListOpen}
        entityCount={selectedCount}
        entityType={entityType}
        lists={compatibleLists}
        isLoading={copyItemsMutation.isPending || createListMutation.isPending}
        onConfirm={handleAddToListConfirm}
        onClose={() => setIsAddToListOpen(false)}
      />
    </PageContainer>
  );
}
